// @ts-nocheck
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import 'katex/dist/katex.min.css';

import PDFControls from './PDFControls';
// import PDFCanvas from './PDFCanvas'; // TODO: 未来集成使用
// import AnnotationOverlay from './AnnotationOverlay'; // TODO: 未来集成使用
import MultiQAPanel from './MultiQAPanel';
import ImageAnalysisPanel from './ImageAnalysisPanel';
import AnnotationsList from './AnnotationsList';
import ModeSelectionDialog from './ModeSelectionDialog';
import StickyNoteDialog from './StickyNoteDialog';
import StickyNotesPanel from './StickyNotesPanel';
import CriticalSummaryPanel from './CriticalSummaryPanel';
import AnalysisReportPanel from './AnalysisReportPanel';
import DocModeRouter from './DocModeRouter';
import { TextHighlighter } from '../utils/textHighlighter';
import { usePDFViewer } from '../hooks/usePDFViewer';
import { useImageAnalysis } from '../hooks/useImageAnalysis';
import { useTTS } from '../hooks/useTTS';
import useKeyboardShortcuts from '../hooks/useKeyboardShortcuts';
import { useStickyNoteDrag } from '../hooks/useStickyNoteDrag';
import SocraticQuestionInput from './SocraticQuestionInput';
import {
  saveAnnotationsToStorage as saveAnnotationsToStorageFn,
  deleteAnnotationFromStorage,
  ANNOTATION_TAGS
} from './pdfUtils';
import { logger } from '../utils/logger';
import { saveReadingProgress, getReadingProgress } from '../utils/readingProgress';
import { apiPost, apiGet } from '../utils/apiClient';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

interface PDFViewerWithAIProps {
  pdfFile: string | null;
  originalFile: File | null;
  onPageChange: (page: number) => void;
  currentPage?: number;
  sessionMode?: boolean;
  currentSession?: any;
  compactMode?: boolean;
  existingFingerprint?: string | null;
}

export default function PDFViewerWithAI({
  pdfFile,
  originalFile,
  onPageChange,
  currentPage = 1,
  sessionMode = false,
  currentSession = null,
  compactMode = false,
  existingFingerprint = null
}: PDFViewerWithAIProps) {
  const {
    // 状态
    numPages, scale, annotations, pdfFingerprint, pdfInfo, isGeneratingFingerprint,
    showNotification, notificationMessage, showAnnotationsList,
    showAnnotationPanel, currentAnnotation, tagFilter, showDeleteConfirm,
    showSummary, summary, generatingSummary,
    pageRef, currentPageAnnotations, totalAnnotations,
    // 多会话问答状态
    qaSessions,
    // 苏格拉底对话状态
    showSocraticDialog, socraticHistory,
    // 方法
    setNumPages, setScale, setAnnotations, setPdfInfo, showNotificationMessage, initializePDF,
    setShowAnnotationsList,
    setTagFilter, setShowDeleteConfirm,
    setAnnotationToDelete, confirmDeleteAnnotation, closeAnnotationPanel, setShowSummary,
    viewAnnotationCallback: viewAnnotationCallbackRaw, copyAnnotationToClipboard,
    createQASession, updateQASession, removeQASession, handleSessionQuestion,
    handleQuickScanAnalysis, handleDeepDiveAnalysis, convertToDeepDive, generateCriticalSummary,
    batchDeleteAnnotations,
    refreshAnnotations,
    // 苏格拉底对话方法
    startSocraticDialogue, handleSocraticQuestion, closeSocraticDialog,
    // 便利贴方法
    createStickyNote
  } = usePDFViewer(originalFile, currentPage, sessionMode, currentSession, existingFingerprint);

  // 图片分析 Hook（截图、AI问答、保存标注）
  const {
    showImagePanel, isSelecting, selectionRect, capturedImage,
    imageQuestion, imageAnswer, imageQuestioning,
    setShowImagePanel, setImageQuestion,
    startImageSelection, cancelImageSelection, retakeImage,
    handleMouseDown, handleImageQuestion,
    saveImageAsAnnotation, closeImagePanel, getSelectionStyle,
  } = useImageAnalysis({
    onNotify: showNotificationMessage,
    getPageRef: () => pageRef.current || null,
    getCurrentPage: () => currentPageRef.current,
    getPdfFingerprint: () => pdfFingerprint,
    getAnnotations: () => annotations,
    setAnnotations,
    saveAnnotationsToStorage: saveAnnotationsToStorageFn,
  });

  // 标注详情面板独立的 TTS 状态
  const { isGenerating: isGeneratingSpeech, currentAudio, generateSpeech: generateSpeechStream, stopAudio } = useTTS();

  // 包装 viewAnnotationCallback，确保打开标注详情时关闭图片面板
  const viewAnnotationCallback = useCallback((annotation: any) => {
    setShowImagePanel(false);
    viewAnnotationCallbackRaw(annotation);
  }, [viewAnnotationCallbackRaw, setShowImagePanel]);

  const highlighterRef = useRef<any>(null);
  const currentPageRef = useRef(currentPage);

  // 模式选择对话框状态
  const [showModeDialog, setShowModeDialog] = useState(false);
  const [pendingSelection, setPendingSelection] = useState<any>(null);

  // 便利贴对话框状态
  const [showStickyNoteDialog, setShowStickyNoteDialog] = useState(false);
  const [stickyNoteContext, setStickyNoteContext] = useState<any>(null);
  const [showStickyNotesPanel, setShowStickyNotesPanel] = useState(false);
  const [showStickyNoteDetail, setShowStickyNoteDetail] = useState(false);
  const [currentStickyNote, setCurrentStickyNote] = useState<any>(null);
  const { stickyNotePosition, setStickyNotePosition, isDraggingStickyNote, setIsDraggingStickyNote, startDrag } = useStickyNoteDrag();

  // 批判性总结面板状态
  const [showCriticalSummaryPanel, setShowCriticalSummaryPanel] = useState(false);

  // 分析报告面板状态 (via DocModeRouter)
  const [showAnalysisReportPanel, setShowAnalysisReportPanel] = useState(false);
  const [docType, setDocType] = useState('academic_paper');

  // Fetch doc_type when PDF fingerprint changes
  useEffect(() => {
    if (!pdfFingerprint) return;
    apiGet(`/api/research/doc-type/${pdfFingerprint}`)
      .then(res => {
        if (res.success && res.doc_type) {
          setDocType(res.doc_type);
        }
      })
      .catch(() => {}); // silently fallback to academic_paper
  }, [pdfFingerprint]);

  // 更新当前页面引用并保存阅读位置
  useEffect(() => {
    currentPageRef.current = currentPage;

    // 保存阅读位置
    if (pdfFingerprint && currentPage && numPages) {
      saveReadingProgress(pdfFingerprint, currentPage, scale);

      // 同时更新后端学习进度
      const updateProgress = async () => {
        try {
          await apiPost('/api/learning-progress/update', {
            fingerprint: pdfFingerprint,
            current_page: currentPage,
            total_pages: numPages
          });
        } catch (error: any) {
          console.error('更新学习进度失败:', error);
        }
      };

      // 使用防抖避免频繁请求
      const timeoutId = setTimeout(updateProgress, 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [currentPage, pdfFingerprint, scale, numPages]);

  // 初始化PDF（只在原始文件变化时执行）
  useEffect(() => {
    if (originalFile) {
      logger.log('🎬 原始文件变化，触发初始化PDF');
      initializePDF();
    }
  }, [originalFile, initializePDF]);

  // 标注加载完成后，恢复阅读位置（只执行一次）
  const hasRestoredRef = useRef(false);

  useEffect(() => {
    // 当 PDF初始化完成（isGeneratingFingerprint 为 false）、有 fingerprint、有页数、且还没恢复过时才执行
    if (!isGeneratingFingerprint && pdfFingerprint && numPages && numPages > 0 && !hasRestoredRef.current) {
      logger.log('🔄 准备恢复阅读位置', {
        fingerprint: pdfFingerprint,
        numPages,
        annotationsCount: Object.keys(annotations).length,
        currentPage,
        isGeneratingFingerprint
      });

      // 添加小延迟，确保标注数据已经渲染到状态中
      setTimeout(() => {
        const savedProgress = getReadingProgress(pdfFingerprint);
        if (savedProgress) {
          const { page, scale: savedScale } = savedProgress;
          logger.log('📖 找到保存的阅读进度:', savedProgress);

          if (page && page <= numPages && page !== currentPage) {
            logger.log(`✅ 恢复阅读位置：第 ${page} 页`);
            onPageChange && onPageChange(page);
            showNotificationMessage(`已恢复到上次阅读的第 ${page} 页`);
          } else {
            logger.log('⚠️ 页面已是目标页或页码无效，无需恢复');
          }

          if (savedScale && savedScale !== scale) {
            logger.log(`✅ 恢复缩放比例: ${savedScale}`);
            setScale(savedScale);
          }
        } else {
          logger.log('ℹ️ 没有找到保存的阅读进度');
        }

        hasRestoredRef.current = true;
      }, 100);
    }
  }, [isGeneratingFingerprint, pdfFingerprint, numPages, annotations, currentPage, scale, onPageChange, showNotificationMessage, setScale]);

  // 处理文本选择 - 使用稳定的回调，避免重复绑定
  const handleTextSelected = useCallback((text: any, ranges: any) => {
    logger.log('🎯🎯🎯 handleTextSelected被调用 🎯🎯🎯');
    logger.log('📝 选中文本:', text);
    logger.log('📍 文本长度:', text.length);
    logger.log('📄 当前页面:', currentPageRef.current);
    logger.log('📊 范围数量:', ranges.length);
    logger.log('🔧 会话模式:', sessionMode);
    logger.log('💼 当前会话:', currentSession ? currentSession.id : 'null');

    const position = calculateSelectionCenter(ranges);
    logger.log('📍 计算位置:', position);

    // 保存选择信息，显示模式选择对话框
    setPendingSelection({ text, ranges, position });
    logger.log('✅ 准备显示模式选择对话框...');
    setShowModeDialog(true);
    logger.log('✅ setShowModeDialog(true) 已调用');
  }, [sessionMode, currentSession]);

  // 处理模式选择
  const handleModeSelect = useCallback(async (mode: any) => {
    setShowModeDialog(false);

    if (!pendingSelection) return;

    const { text, ranges, position } = pendingSelection;

    // 处理便利贴模式
    if (mode === 'sticky_note') {
      setStickyNoteContext({
        selectedText: text,
        position: position
      });
      setShowStickyNoteDialog(true);
      setPendingSelection(null);
      return;
    }

    // 处理发送到画布模式
    if (mode === 'send_to_canvas') {
      if (!sessionMode || !currentSession) {
        showNotificationMessage('请先进入阅读会话才能使用画布功能');
        setPendingSelection(null);
        return;
      }

      try {
        // 创建 PDF 片段块
        const blockResponse = await apiPost('/api/blocks/', {
          type: 'pdf_snippet',
          session_id: currentSession.id,
          pdf_fingerprint: pdfFingerprint,
          page: currentPageRef.current,
          content: {
            text: text,
            page: currentPageRef.current,
            source: pdfInfo?.fileName || 'PDF文档'
          },
          position: {
            x: 100 + Math.random() * 200,
            y: 100 + Math.random() * 200
          },
          size: {
            width: 280,
            height: 200
          }
        });

        logger.log('✅ Canvas PDF片段块创建成功:', blockResponse);
        showNotificationMessage(`已将选中内容发送到画布 (第${currentPageRef.current}页)`);
      } catch (error: any) {
        console.error('❌ 创建Canvas块失败:', error);
        showNotificationMessage('发送到画布失败: ' + error.message);
      }

      setPendingSelection(null);
      return;
    }

    // 处理其他模式
    const sessionId = createQASession(text, position, mode);

    if (sessionId) {
      // 将高亮数据保存到会话中
      updateQASession(sessionId, {
        highlightRanges: ranges,
        hasHighlight: true
      });

      setShowImagePanel(false);

      if (mode === 'quick_scan') {
        showNotificationMessage(`已在第${currentPageRef.current}页创建快速浏览会话，AI正在分析...`);
      } else if (mode === 'normal') {
        showNotificationMessage(`已在第${currentPageRef.current}页创建问答会话`);
      } else if (mode === 'manual') {
        showNotificationMessage(`已在第${currentPageRef.current}页创建手动标注`);
      }
    }

    setPendingSelection(null);
  }, [pendingSelection, createQASession, updateQASession, setShowImagePanel, showNotificationMessage, sessionMode, currentSession, pdfFingerprint, pdfInfo]);

  // 取消模式选择
  const handleModeCancel = useCallback(() => {
    setShowModeDialog(false);
    setPendingSelection(null);

    // 清除文本选择
    if (window.getSelection) {
      window.getSelection()?.removeAllRanges();
    }
  }, []);

  // 发送分析结果到画布
  const handleSendToCanvas = useCallback(async (sessionId: any, analysisType: any) => {
    if (!sessionMode || !currentSession) {
      showNotificationMessage('请先进入阅读会话才能使用画布功能');
      return;
    }

    // 找到对应的会话
    const session = qaSessions.find(s => s.id === sessionId);
    if (!session) {
      showNotificationMessage('找不到对应的会话');
      return;
    }

    // 🔧 修复：检查是否已发送到画布，避免重复创建
    const sentKey = analysisType === 'deep_dive' ? 'sentDeepDiveToCanvas' : 'sentQuickScanToCanvas';
    if (session[sentKey]) {
      showNotificationMessage('该分析结果已发送到画布，请勿重复发送');
      return;
    }

    const { editableText, aiQuickAnalysis, aiDeepAnalysis, page } = session;
    const analysis = analysisType === 'deep_dive' ? aiDeepAnalysis : aiQuickAnalysis;

    if (!analysis) {
      showNotificationMessage('请先完成分析再发送到画布');
      return;
    }

    try {
      const blockType = analysisType === 'deep_dive' ? 'qa_deep_dive' : 'qa_quick_scan';
      const blockResponse = await apiPost('/api/blocks/', {
        type: blockType,
        session_id: currentSession.id,
        pdf_fingerprint: pdfFingerprint,
        page: page || currentPageRef.current,
        content: {
          selected_text: editableText,
          analysis: analysis,
          source: pdfInfo?.fileName || 'PDF文档'
        },
        position: {
          x: 100 + Math.random() * 300,
          y: 100 + Math.random() * 200
        },
        size: {
          width: analysisType === 'deep_dive' ? 320 : 280,
          height: analysisType === 'deep_dive' ? 240 : 200
        }
      });

      logger.log('✅ Canvas 分析块创建成功:', blockResponse);

      // 🔧 修复：标记已发送到画布，防止重复创建
      updateQASession(sessionId, { [sentKey]: true });

      const typeName = analysisType === 'deep_dive' ? '深入理解' : '快速浏览';
      showNotificationMessage(`已将${typeName}结果发送到画布`);
    } catch (error: any) {
      console.error('❌ 创建Canvas块失败:', error);
      showNotificationMessage('发送到画布失败: ' + error.message);
    }
  }, [sessionMode, currentSession, qaSessions, pdfFingerprint, pdfInfo, showNotificationMessage, updateQASession]);

  // 保存便利贴
  const handleSaveStickyNote = useCallback(async (noteData: any) => {
    const success = await createStickyNote({
      noteType: noteData.noteType,
      content: noteData.content,
      selectedText: stickyNoteContext?.selectedText,
      page: currentPageRef.current,
      position: stickyNoteContext?.position
    });

    if (success) {
      setShowStickyNoteDialog(false);
      setStickyNoteContext(null);
    }
  }, [createStickyNote, stickyNoteContext]);

  // 取消便利贴
  const handleCancelStickyNote = useCallback(() => {
    setShowStickyNoteDialog(false);
    setStickyNoteContext(null);
  }, []);

  // 从工具栏打开便利贴（无选中文本）
  const handleOpenStickyNoteFromToolbar = useCallback(() => {
    setStickyNoteContext({
      selectedText: '', // 无选中文本
      position: { x: 150, y: 150 }
    });
    setShowStickyNoteDialog(true);
  }, []);

  // 处理高亮点击 - 稳定的回调
  const handleHighlightClick = useCallback((annotation: any) => {
    viewAnnotationCallback(annotation);
  }, [viewAnnotationCallback]);

  // 快捷键处理
  useKeyboardShortcuts({
    SAVE_SESSION: () => {
      // 如果有活跃的会话，保存第一个
      if (qaSessions && qaSessions.length > 0) {
        const activeSession = qaSessions[0];
        if (activeSession && activeSession.status !== 'ready') {
          // 触发保存逻辑
          logger.log('快捷键触发保存会话:', activeSession.id);
          showNotificationMessage('Ctrl+S: 保存会话功能');
        }
      }
    },
    CLOSE_DIALOG: () => {
      // 关闭各种弹窗
      if (showAnnotationPanel) {
        closeAnnotationPanel();
      } else if (showAnnotationsList) {
        setShowAnnotationsList(false);
      } else if (showImagePanel) {
        closeImagePanel();
      } else if (showModeDialog) {
        handleModeCancel();
      }
    },
    SEARCH: () => {
      // 打开标注列表并聚焦搜索框
      setShowAnnotationsList(true);
      setTimeout(() => {
        const searchInput = document.querySelector('.search-input');
        if (searchInput) (searchInput as HTMLElement).focus();
      }, 100);
    },
    NEXT_PAGE: () => {
      // 下一页
      if (numPages && currentPage < numPages) {
        onPageChange(currentPage + 1);
      }
    },
    PREV_PAGE: () => {
      // 上一页
      if (currentPage > 1) {
        onPageChange(currentPage - 1);
      }
    },
    NEXT_PAGE_SPACE: () => {
      // 空格键下一页（如果不在输入框中）
      if (numPages && currentPage < numPages) {
        onPageChange(currentPage + 1);
      }
    },
  }, {
    enabled: true,
    preventDefault: true,
    excludeInputs: true,
  });

  /**
   * 统一的高亮器初始化函数
   * 使用 MutationObserver 替代固定超时，更精确地检测 textLayer 就绪
   */
  const initHighlighter = useCallback(async () => {
    if (!pageRef.current || isSelecting) return;

    // 销毁旧的高亮器
    if (highlighterRef.current) {
      highlighterRef.current.destroy();
      highlighterRef.current = null;
    }

    try {
      // 使用 MutationObserver 等待 textLayer 就绪
      await TextHighlighter.waitForTextLayer(pageRef.current, 1500);

      // 再次检查条件（可能在等待期间发生变化）
      if (!pageRef.current || highlighterRef.current || isSelecting) return;

      highlighterRef.current = new TextHighlighter(pageRef.current, {
        highlightColor: 'rgba(255, 235, 59, 0.4)',
        selectedColor: 'rgba(33, 150, 243, 0.3)',
        borderColor: '#FFC107'
      });

      highlighterRef.current.onTextSelected = handleTextSelected;
      highlighterRef.current.onHighlightClick = handleHighlightClick;

      logger.log('✅ 高亮器初始化成功');

      // 立即恢复高亮（无需额外延迟）
      restorePageHighlights();
    } catch (error: any) {
      console.error('❌ 高亮器初始化失败:', error);
    }
  }, [isSelecting, handleTextSelected, handleHighlightClick]);

  // 页面变化时重新初始化高亮器
  useEffect(() => {
    if (pageRef.current && !isSelecting) {
      initHighlighter();
    }

    return () => {
      if (highlighterRef.current) {
        highlighterRef.current.destroy();
        highlighterRef.current = null;
      }
    };
  }, [currentPage, scale, initHighlighter]);

  // 标注数据变化时恢复高亮（无需延迟）
  useEffect(() => {
    if (highlighterRef.current && Object.keys(annotations).length > 0) {
      restorePageHighlights();
    }
  }, [annotations]);

  // 页面切换时只清理文本选择，不重置截图状态
  useEffect(() => {
    if (pageRef.current) {
      if (window.getSelection) {
        window.getSelection()?.removeAllRanges();
      }
    }
  }, [currentPage]);

  // 截图状态变化时绑定/解绑事件
  useEffect(() => {
    if (!pageRef.current) return;

    const pageElement = pageRef.current;

    if (isSelecting) {
      logger.log('开始截图模式');
      pageElement.style.cursor = 'crosshair';
      pageElement.style.userSelect = 'none';

      // 移除文本选择事件避免冲突
      if (highlighterRef.current) {
        highlighterRef.current.destroy();
        highlighterRef.current = null;
      }
    } else {
      logger.log('退出截图模式');
      pageElement.style.cursor = 'default';
      pageElement.style.userSelect = 'auto';

      // 使用统一的初始化函数重新初始化高亮器
      if (!highlighterRef.current) {
        initHighlighter();
      }
    }
  }, [isSelecting, initHighlighter]);

  // 监听标注点击事件
  useEffect(() => {
    const handleAnnotationClick = (event: any) => {
      const { annotation } = event.detail;
      viewAnnotationCallback(annotation);
    };
    
    document.addEventListener('annotationClick', handleAnnotationClick);
    
    return () => {
      document.removeEventListener('annotationClick', handleAnnotationClick);
    };
  }, [viewAnnotationCallback]);

  const onDocumentLoadSuccess = ({ numPages }: any) => {
    setNumPages(numPages);

    if (pdfFingerprint && originalFile) {
      // 🔧 移除冗余的 savePDFInfo 调用
      // PDF信息已在上传时由后端 /api/upload 保存到数据库
      setPdfInfo({
        fileName: originalFile.name,
        fileSize: originalFile.size,
        totalPages: numPages,
        fingerprint: pdfFingerprint
      });

      // 阅读位置恢复逻辑已移至 initAndRestore 中执行，避免在标注加载前恢复
      logger.log('ℹ️ PDF文档加载完成，阅读位置将在标注加载后恢复');
    }
  };

  // 计算选择区域的中心点
  const calculateSelectionCenter = (ranges: any) => {
    let totalX = 0, totalY = 0, rectCount = 0;
    
    ranges.forEach((range: any) => {
      range.rects.forEach((rect: any) => {
        totalX += rect.left + rect.width / 2;
        totalY += rect.top + rect.height / 2;
        rectCount++;
      });
    });
    
    return rectCount > 0 ? {
      x: totalX / rectCount,
      y: totalY / rectCount
    } : { x: 100, y: 100 };
  };

  // 恢复页面高亮
  const restorePageHighlights = () => {
    if (!highlighterRef.current) {
      logger.log('⚠️ 高亮器未初始化，无法恢复高亮');
      return;
    }

    const allAnnotations = Object.values(annotations);

    // 调试：打印所有标注的 page 值
    logger.log('🔍 调试 - 所有标注:', allAnnotations.map(ann => ({
      id: ann.id,
      page: ann.page,
      pageType: typeof ann.page,
      hasHighlight: ann.hasHighlight,
      hasRects: !!ann.highlightRects,
      rectsLength: ann.highlightRects?.length
    })));
    logger.log('🔍 调试 - currentPage:', currentPage, 'type:', typeof currentPage);

    // 获取所有当前页的标注（使用宽松比较以处理类型不一致）
    // 只显示有明确页码且匹配当前页的标注
    const allPageAnnotations = allAnnotations.filter(
      ann => ann.page != null && Number(ann.page) === Number(currentPage)
    );

    // 过滤出有高亮数据的标注
    const currentPageAnnotations = allPageAnnotations.filter(
      ann => ann.hasHighlight && ann.highlightRects && ann.highlightRects.length > 0
    );

    logger.log('📍 恢复页面高亮:', {
      当前页面: currentPage,
      总标注数: allAnnotations.length,
      当前页所有标注: allPageAnnotations.length,
      有高亮的标注: currentPageAnnotations.length,
      无高亮数据的标注: allPageAnnotations
        .filter(ann => !ann.hasHighlight || !ann.highlightRects)
        .map(ann => ({ id: ann.id, hasHighlight: ann.hasHighlight, hasRects: !!ann.highlightRects }))
    });

    if (currentPageAnnotations.length > 0) {
      highlighterRef.current.restoreHighlights(currentPageAnnotations);
    }
  };

  // 修改保存标注逻辑，支持高亮数据
  const saveSessionAsAnnotationWithHighlight = useCallback(async (sessionId: any) => {
    const session = qaSessions.find(s => s.id === sessionId);
    if (!session) {
      showNotificationMessage('会话不存在');
      return;
    }

    // 检查是否有内容可以保存
    const hasContent = session.aiQuickAnalysis || session.aiDeepAnalysis || (session.answer && session.answer.trim().length > 0);
    if (!hasContent) {
      showNotificationMessage('请先获取AI分析或回答');
      return;
    }

    if (!pdfFingerprint) {
      showNotificationMessage('PDF指纹生成中，请稍候重试');
      return;
    }

    try {
      // 判断保存类型
      const isQuickScanOnly = session.mode === 'quick_scan' && !session.aiDeepAnalysis;
      const isDeepDiveUpdate = (session.mode === 'deep_dive' || session.aiDeepAnalysis) && session.quickScanSaved && session.savedAnnotationId;
      
      let annotationId: string;
      let shouldRemoveSession = true;
      
      // 如果是更新已保存的快速浏览标注
      if (isDeepDiveUpdate) {
        annotationId = session.savedAnnotationId!;
        logger.log('更新已保存的快速浏览标注:', annotationId);
      } else {
        annotationId = `${session.page}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        logger.log('创建新标注:', annotationId);
      }
      
      let relativePosition = null;
      if (session.position) {
        relativePosition = {
          x: session.position.x / scale,
          y: session.position.y / scale
        };
      }

      // 处理高亮矩形数据
      let highlightRects: any[] = [];
      if (session.highlightRanges && session.highlightRanges.length > 0) {
        session.highlightRanges.forEach((range: any) => {
          range.rects.forEach((rect: any) => {
            highlightRects.push({
              left: rect.left,
              top: rect.top,
              width: rect.width,
              height: rect.height
            });
          });
        });
      }

      // 构建最终问答内容
      let finalQuestion, finalAnswer, sessionType, subType;

      // 如果是导读模式
      if (session.mode === 'quick_scan' && session.aiQuickAnalysis && !session.aiDeepAnalysis) {
        finalQuestion = `快速浏览：${session.sessionName || '未命名'}`;
        finalAnswer = session.aiQuickAnalysis;
        sessionType = 'guide';
        subType = 'guide.quick_scan';
        shouldRemoveSession = false;
      } else if (session.aiDeepAnalysis) {
        finalQuestion = `深入理解：${session.sessionName || '未命名'}`;
        if (session.aiQuickAnalysis) {
          finalAnswer = `【第一轮快速浏览】\n${session.aiQuickAnalysis}\n\n【第二轮深入理解】\n${session.aiDeepAnalysis}`;
        } else {
          finalAnswer = session.aiDeepAnalysis;
        }
        sessionType = 'guide';
        subType = 'guide.deep_dive';
        shouldRemoveSession = true;
      } else if (session.conversationHistory && session.conversationHistory.length >= 2) {
        const conversations = [];
        const history = session.conversationHistory;

        for (let i = 1; i < history.length; i += 2) {
          if (history[i] && history[i + 1]) {
            const userMsg = history[i].content.replace(/基于以下文档内容回答问题：.*?请根据文档内容准确回答问题[^。]*。回答要简洁准确。/s, '').trim();
            const assistantMsg = history[i + 1].content;
            conversations.push(`问: ${userMsg}\n答: ${assistantMsg}`);
          }
        }

        finalQuestion = session.sessionName || '多轮对话记录';
        finalAnswer = conversations.join('\n\n---\n\n');
        sessionType = 'qa';
        subType = 'qa.conversation';
        shouldRemoveSession = true;
      } else if (session.mode === 'manual') {
        // 手动标注
        finalQuestion = session.sessionName || '手动标注';
        finalAnswer = session.answer || session.editableText;
        sessionType = 'manual';
        const tag = session.selectedTag || 'note';
        subType = `manual.${tag}`;
        shouldRemoveSession = true;
      } else {
        // 普通问答
        finalQuestion = session.sessionName || (session.question && session.question.trim() ? session.question : '文档问答');
        finalAnswer = session.answer;
        sessionType = 'qa';
        subType = 'qa.text';
        shouldRemoveSession = true;
      }

      const newAnnotation = {
        id: annotationId,
        page: session.page,
        text: session.editableText,
        question: finalQuestion,
        answer: finalAnswer,
        tag: session.selectedTag || 'note',
        timestamp: isDeepDiveUpdate && annotationId ? annotations[annotationId]?.timestamp : new Date().toISOString(),
        pdfFingerprint: pdfFingerprint,
        position: relativePosition,
        type: 'text',
        highlightRects: highlightRects,
        hasHighlight: session.hasHighlight || false,
        sessionType: sessionType,
        subType: subType,
        sessionName: session.sessionName,
        readingSessionId: sessionMode && currentSession ? currentSession.id : null  // 添加会话ID
      };

      const newAnnotations = {
        ...annotations,
        [annotationId]: newAnnotation
      };

      const saved = await saveAnnotationsToStorageFn(pdfFingerprint, newAnnotations) as any;
      
      if (saved) {
        setAnnotations(newAnnotations);
        
        // 确认高亮显示
        if (session.hasHighlight && highlighterRef.current) {
          highlighterRef.current.confirmHighlight(annotationId, newAnnotation);
        }
        
        let messageText;
        if (isQuickScanOnly) {
          messageText = `快速浏览已保存到第${session.page}页（可继续深入理解）`;
          shouldRemoveSession = false;
        } else if (isDeepDiveUpdate) {
          messageText = `深入理解已更新到第${session.page}页`;
          shouldRemoveSession = true;
        } else if (session.mode === 'deep_dive') {
          messageText = `深入理解已保存到第${session.page}页`;
          shouldRemoveSession = true;
        } else if (session.conversationHistory && session.conversationHistory.length >= 2) {
          messageText = `多轮对话已保存到第${session.page}页`;
          shouldRemoveSession = true;
        } else {
          messageText = `问答已保存到第${session.page}页`;
          shouldRemoveSession = true;
        }
        
        if (shouldRemoveSession) {
          removeQASession(sessionId);
        } else {
          updateQASession(sessionId, {
            quickScanSaved: true,
            savedAnnotationId: annotationId
          });
        }

        // 🔧 修复：移除重复的 block 创建逻辑
        // saveAnnotationsToStorage 已经通过 annotationToBlockFormat 创建了 block
        // 这里不需要再创建，否则会导致重复（每次保存都会多创建一个）
        // 如果用户需要单独发送到画布，可以使用"发送到画布"按钮
        logger.log('📝 标注已通过 saveAnnotationsToStorage 保存为 block，跳过重复创建');

        showNotificationMessage(messageText);
      } else {
        showNotificationMessage('保存失败，请重试');
      }
    } catch (error: any) {
      console.error('保存标注时出错:', error);
      showNotificationMessage('保存失败: ' + error.message);
    }
  }, [pdfFingerprint, scale, annotations, showNotificationMessage, removeQASession, updateQASession, qaSessions, setAnnotations]);

  // 页面加载成功后的处理（简化版）
  const onPageLoadSuccess = useCallback(() => {
    logger.log('📄 页面加载完成');
    // 高亮器初始化由 useEffect 统一处理，无需在这里重复
    // initHighlighter 会通过 MutationObserver 等待 textLayer 就绪
  }, []);

  // 缩放控制
  const zoomIn = () => setScale(prev => Math.min(prev + 0.25, 3.0));
  const zoomOut = () => setScale(prev => Math.max(prev - 0.25, 0.5));
  const resetZoom = () => setScale(1.0);

  // 翻页控制
  const goToPrevPage = () => {
    if (currentPage > 1) {
      const newPage = currentPage - 1;
      onPageChange && onPageChange(newPage);
    }
  };

  const goToNextPage = () => {
    if (currentPage < (numPages || 1)) {
      const newPage = currentPage + 1;
      onPageChange && onPageChange(newPage);
    }
  };

  // 删除标注
  const deleteAnnotation = (annotationId: any) => {
    setAnnotationToDelete(annotationId);
    setShowDeleteConfirm(true);
  };

  // 获取便利贴列表
  const stickyNotes = Object.values(annotations).filter(ann => ann.type === 'sticky_note' && !ann.isDeleted);

  return (
    <div className="pdf-viewer-container">
      <PDFControls
        currentPage={currentPage}
        numPages={numPages}
        scale={scale}
        totalAnnotations={totalAnnotations}
        currentPageAnnotations={currentPageAnnotations}
        pdfFingerprint={pdfFingerprint}
        isGeneratingFingerprint={isGeneratingFingerprint}
        onShowAnnotationsList={() => setShowAnnotationsList(!showAnnotationsList)}
        onShowImagePanel={() => {
          const newShowImagePanel = !showImagePanel;
          setShowImagePanel(newShowImagePanel);
          if (!newShowImagePanel) {
            closeImagePanel();
          }
        }}
        onGenerateCriticalSummary={generateCriticalSummary}
        onShowStickyNote={handleOpenStickyNoteFromToolbar}
        onToggleStickyNotesPanel={() => setShowStickyNotesPanel(!showStickyNotesPanel)}
        onToggleCriticalSummaryPanel={() => {
          const next = !showCriticalSummaryPanel;
          setShowCriticalSummaryPanel(next);
          if (next) setShowAnalysisReportPanel(false);
        }}
        onPageChange={onPageChange}
        onZoomIn={zoomIn}
        onZoomOut={zoomOut}
        onResetZoom={resetZoom}
        generatingSummary={generatingSummary}
        showImagePanel={showImagePanel}
        showStickyNotesPanel={showStickyNotesPanel}
        showCriticalSummaryPanel={showCriticalSummaryPanel}
        onToggleAnalysisReportPanel={() => {
          const next = !showAnalysisReportPanel;
          setShowAnalysisReportPanel(next);
          if (next) setShowCriticalSummaryPanel(false);
        }}
        showAnalysisReportPanel={showAnalysisReportPanel}
        deepDiveCount={qaSessions.filter(s => s.mode === 'deep_dive' && s.aiDeepAnalysis).length}
        criticalSummaryCount={Object.values(annotations).filter(ann => ann.subType === 'guide.critical_summary' && !ann.isDeleted).length}
      />

      <div className="pdf-document-container">
        <Document
          file={pdfFile}
          onLoadSuccess={onDocumentLoadSuccess}
          loading={
            <div className="loading-container">
              <div className="spinner"></div>
              <p>加载PDF中...</p>
              {isGeneratingFingerprint && (
                <p style={{ fontSize: '12px', color: '#666' }}>正在生成文件指纹...</p>
              )}
            </div>
          }
          error={
            <div className="error-container">
              <p>PDF加载失败</p>
            </div>
          }
        >
          <div className="pdf-page-container" style={{ display: 'flex', justifyContent: 'center', position: 'relative' }}>
            <button 
              className="page-nav-btn page-nav-left"
              onClick={goToPrevPage} 
              disabled={currentPage <= 1}
              title="上一页"
            >
              ‹
            </button>
            
            <div
              ref={pageRef}
              className="page-wrapper"
              style={{
                position: 'relative',
                cursor: isSelecting ? 'crosshair' : 'default',
                userSelect: isSelecting ? 'none' : 'auto'
              }}
              onMouseDown={isSelecting ? handleMouseDown : undefined}
            >
              <Page
                pageNumber={currentPage}
                onLoadSuccess={onPageLoadSuccess}
                renderTextLayer={true}
                renderAnnotationLayer={true}
                scale={scale}
                width={Math.min(window.innerWidth - 100, 800)}
              />
              
              {isSelecting && selectionRect && (
                <div 
                  className="selection-overlay"
                  style={getSelectionStyle()}
                />
              )}
              
              {isSelecting && (
                <div style={{
                  position: 'absolute',
                  top: '10px',
                  left: '10px',
                  background: 'rgba(0,0,0,0.8)',
                  color: 'white',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  fontSize: '14px',
                  zIndex: 1000,
                  pointerEvents: 'none'
                }}>
                  截图模式：请在页面上拖拽选择区域
                </div>
              )}

            </div>
            
            <button 
              className="page-nav-btn page-nav-right"
              onClick={goToNextPage} 
              disabled={currentPage >= (numPages || 1)}
              title="下一页"
            >
              ›
            </button>
          </div>
        </Document>
      </div>

      {!compactMode && (
        <MultiQAPanel
          qaSessions={qaSessions}
          onUpdateSession={updateQASession}
          onRemoveSession={removeQASession}
          onAskQuestion={handleSessionQuestion}
          onSaveAsAnnotation={saveSessionAsAnnotationWithHighlight}
          onStartQuickScan={handleQuickScanAnalysis}
          onStartDeepDive={handleDeepDiveAnalysis}
          onConvertToDeepDive={convertToDeepDive}
          onSendToCanvas={handleSendToCanvas}
        />
      )}

      {!compactMode && (
        <ImageAnalysisPanel
          show={showImagePanel}
          isSelecting={isSelecting}
          capturedImage={capturedImage}
          imageQuestion={imageQuestion}
          imageAnswer={imageAnswer}
          imageQuestioning={imageQuestioning}
          selectedTag="note"
          onClose={closeImagePanel}
          onStartSelection={startImageSelection}
          onCancelSelection={cancelImageSelection}
          onQuestionChange={setImageQuestion}
          onAnalyze={handleImageQuestion}
          onTagChange={() => {}}
          onSaveAnnotation={saveImageAsAnnotation}
          onRetake={retakeImage}
        />
      )}

      {!compactMode && (
        <AnnotationsList
          show={showAnnotationsList}
          annotations={annotations}
          tagFilter={tagFilter}
          pdfInfo={pdfInfo}
          onClose={() => setShowAnnotationsList(false)}
          onTagFilterChange={setTagFilter}
          onViewAnnotation={(annotation: any) => {
            onPageChange && onPageChange(annotation.page);
            viewAnnotationCallback(annotation);
            setShowAnnotationsList(false);
          }}
          onBatchDelete={batchDeleteAnnotations}
          onRefresh={refreshAnnotations as any}
        />
      )}

      {showNotification && (
        <div className="notification">
          {notificationMessage}
        </div>
      )}

      {showAnnotationPanel && currentAnnotation && (
        <div className="annotation-overlay">
          <div className="annotation-panel">
            <div className="annotation-panel-header">
              <h4>标注详情</h4>
              <button onClick={closeAnnotationPanel} className="close-btn">×</button>
            </div>
            
            <div className="annotation-details">
              <div className="annotation-meta">
                <span className="page-info">第 {currentAnnotation.page} 页</span>
                <span className="annotation-tag" style={{
                  backgroundColor: ((ANNOTATION_TAGS as any)[currentAnnotation.tag] || ANNOTATION_TAGS.note).color,
                  color: 'white',
                  padding: '3px 8px',
                  borderRadius: '4px',
                  fontSize: '12px'
                }}>
                  {((ANNOTATION_TAGS as any)[currentAnnotation.tag] || ANNOTATION_TAGS.note).name}
                </span>
                {currentAnnotation.type === 'image' && (
                  <span className="image-indicator">图片标注</span>
                )}
                {currentAnnotation.hasHighlight && (
                  <span className="highlight-indicator">高亮标注</span>
                )}
                <span className="date-info">
                  {new Date(currentAnnotation.timestamp).toLocaleString()}
                </span>
              </div>
              
              {currentAnnotation.type === 'image' && currentAnnotation.image && (
                <div className="annotation-image">
                  <strong>截图内容：</strong>
                  <img src={currentAnnotation.image} alt="标注截图" className="annotation-preview-image" />
                </div>
              )}
              
              {currentAnnotation.type !== 'image' && (
                <div className="annotation-text">
                  <strong>选中内容：</strong>
                  <p>"{currentAnnotation.text}"</p>
                </div>
              )}
              
              <div className="annotation-question">
                <strong>问题：</strong>
                <p>{currentAnnotation.question}</p>
              </div>
              
              <div className="annotation-answer">
                <div className="answer-header">
                  <strong>AI回答：</strong>
                  <div className="speech-controls">
                    <button
                      onClick={() => copyAnnotationToClipboard(currentAnnotation.answer)}
                      className="copy-btn"
                      title="复制到剪贴板"
                      style={{ marginRight: '4px' }}
                    >
                      📋
                    </button>
                    <button
                      onClick={() => generateSpeechStream(currentAnnotation.answer)}
                      disabled={isGeneratingSpeech}
                      className="speech-btn"
                      title="转为语音"
                    >
                      {isGeneratingSpeech ? '生成中...' : '🔊'}
                    </button>
                    {currentAudio && (
                      <button
                        onClick={stopAudio}
                        className="stop-speech-btn"
                        title="停止播放"
                      >
                        ⏹
                      </button>
                    )}
                  </div>
                </div>
                <div className="markdown-content">
                  <ReactMarkdown 
                    remarkPlugins={[remarkMath]} 
                    rehypePlugins={[rehypeKatex]}
                  >
                    {currentAnnotation.answer}
                  </ReactMarkdown>
                </div>
              </div>
              
              <div className="annotation-actions">
                <button 
                  onClick={() => deleteAnnotation(currentAnnotation.id)}
                  className="delete-btn"
                >
                  删除标注
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showSummary && (
        <div className="summary-overlay">
          <div className="summary-panel">
            <div className="summary-header">
              <h4>文档摘要</h4>
              <button onClick={() => setShowSummary(false)} className="close-btn">×</button>
            </div>
            <div className="summary-content">
              {summary && (
                <div className="summary-text">
                  <ReactMarkdown 
                    remarkPlugins={[remarkMath]} 
                    rehypePlugins={[rehypeKatex]}
                  >
                    {summary}
                  </ReactMarkdown>
                </div>
              )}
            </div>
            <div className="summary-footer">
              <small>基于 {totalAnnotations} 个标注生成</small>
              <button
                onClick={() => {
                  startSocraticDialogue(summary);
                  setShowSummary(false);
                }}
                className="socratic-btn"
                style={{
                  marginTop: '10px',
                  padding: '8px 16px',
                  background: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                🧠 继续深入：AI导师提问
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 苏格拉底对话界面 */}
      {showSocraticDialog && (
        <div className="summary-overlay">
          <div className="summary-panel" style={{maxWidth: '800px'}}>
            <div className="summary-header">
              <h4>🧠 AI导师引导对话</h4>
              <button onClick={closeSocraticDialog} className="close-btn">×</button>
            </div>
            <div className="summary-content" style={{maxHeight: '500px', overflowY: 'auto'}}>
              {socraticHistory.map((msg, index) => (
                <div key={index} style={{
                  marginBottom: '15px',
                  padding: '10px',
                  background: msg.role === 'user' ? '#E3F2FD' : '#F5F5F5',
                  borderRadius: '8px',
                  borderLeft: msg.role === 'user' ? '4px solid #2196F3' : '4px solid #4CAF50'
                }}>
                  <strong>{msg.role === 'user' ? '你' : 'AI导师'}：</strong>
                  <ReactMarkdown
                    remarkPlugins={[remarkMath]}
                    rehypePlugins={[rehypeKatex]}
                  >
                    {msg.content}
                  </ReactMarkdown>
                </div>
              ))}
            </div>
            <div className="summary-footer">
              <SocraticQuestionInput
                onSubmit={handleSocraticQuestion as any}
                disabled={socraticHistory.length >= 10}
              />
              {socraticHistory.length >= 10 && (
                <small style={{color: '#666', marginTop: '10px', display: 'block'}}>
                  对话已结束。点击关闭保存本次对话记录。
                </small>
              )}
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="confirm-overlay">
          <div className="confirm-dialog">
            <h4>确认删除</h4>
            <p>确定要删除这个标注吗？此操作无法撤销。</p>
            <div className="confirm-actions">
              <button onClick={() => {
                setShowDeleteConfirm(false);
                setAnnotationToDelete(null);
              }} className="cancel-btn">
                取消
              </button>
              <button onClick={confirmDeleteAnnotation} className="confirm-btn">
                确认删除
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 模式选择对话框 */}
      <ModeSelectionDialog
        show={showModeDialog}
        selectedText={pendingSelection?.text || ''}
        onSelect={handleModeSelect}
        onCancel={handleModeCancel}
      />

      {/* 便利贴对话框 */}
      <StickyNoteDialog
        show={showStickyNoteDialog}
        selectedText={stickyNoteContext?.selectedText || ''}
        onSave={handleSaveStickyNote}
        onCancel={handleCancelStickyNote}
      />

      {/* 便利贴展开按钮 - 收起时显示 */}
      {!compactMode && !showStickyNotesPanel && (
        <button
          onClick={() => setShowStickyNotesPanel(true)}
          style={{
            position: 'fixed',
            right: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            width: '32px',
            height: '80px',
            background: '#fff',
            border: '1px solid #e0e0e0',
            borderRight: 'none',
            borderRadius: '4px 0 0 4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '18px',
            color: '#666',
            boxShadow: '-2px 0 4px rgba(0,0,0,0.1)',
            transition: 'all 0.2s',
            zIndex: 999
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#f5f5f5';
            e.currentTarget.style.color = '#333';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#fff';
            e.currentTarget.style.color = '#666';
          }}
          title="展开便利贴"
        >
          ‹
        </button>
      )}

      {/* 便利贴侧边栏 - 从右侧滑入 */}
      {!compactMode && showStickyNotesPanel && (
        <div style={{
          position: 'fixed',
          right: 0,
          top: 0,
          bottom: 0,
          width: '350px',
          zIndex: 1000,
          background: '#fff',
          boxShadow: '-2px 0 8px rgba(0,0,0,0.1)',
          animation: 'slideInRight 0.3s ease-out'
        }}>
          {/* 收缩按钮 - 在侧边栏左侧 */}
          <button
            onClick={() => setShowStickyNotesPanel(false)}
            style={{
              position: 'absolute',
              left: '-40px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '32px',
              height: '80px',
              background: '#fff',
              border: '1px solid #e0e0e0',
              borderRight: 'none',
              borderRadius: '4px 0 0 4px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px',
              color: '#666',
              boxShadow: '-2px 0 4px rgba(0,0,0,0.05)',
              transition: 'all 0.2s',
              zIndex: 1
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#f5f5f5';
              e.currentTarget.style.color = '#333';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#fff';
              e.currentTarget.style.color = '#666';
            }}
            title="收起便利贴"
          >
            ›
          </button>

          <StickyNotesPanel
            stickyNotes={stickyNotes as any}
            currentPage={currentPage}
            onCreateNote={async (noteData) => {
              const success = await createStickyNote({
                noteType: noteData.noteType,
                content: noteData.content,
                page: noteData.page,
                selectedText: '',
                position: { x: 0, y: 0 }
              });
              return success;
            }}
            onDeleteNote={async (noteId) => {
              try {
                // 使用新的 blocks API 删除
                const result = await deleteAnnotationFromStorage(noteId) as any;
                if (result.success) {
                  setAnnotations(prev => {
                    const newAnnotations = { ...prev };
                    delete newAnnotations[noteId];
                    return newAnnotations;
                  });
                }
              } catch (error: any) {
                console.error('删除便利贴失败:', error);
              }
            }}
            onViewNote={(note) => {
              setCurrentStickyNote(note);
              setShowStickyNoteDetail(true);
              // 初始化位置为屏幕中央
              setStickyNotePosition({
                x: window.innerWidth / 2 - 225, // 450px 宽度的一半
                y: window.innerHeight / 2 - 150 // 大约窗口高度的一半
              });
              setIsDraggingStickyNote(false);
            }}
          />
        </div>
      )}

      {/* 批判性总结面板 */}
      {!compactMode && showCriticalSummaryPanel && (
        <CriticalSummaryPanel
          show={showCriticalSummaryPanel}
          annotations={annotations}
          pdfInfo={pdfInfo}
          onClose={() => setShowCriticalSummaryPanel(false)}
          onDelete={async (summaryId) => {
            try {
              // 使用新的 blocks API 删除
              const result = await deleteAnnotationFromStorage(summaryId) as any;
              if (result.success) {
                setAnnotations(prev => {
                  const newAnnotations = { ...prev };
                  delete newAnnotations[summaryId];
                  return newAnnotations;
                });
                showNotificationMessage('批判性总结已删除');
              } else {
                showNotificationMessage('删除失败: ' + result.message);
              }
            } catch (error: any) {
              console.error('删除批判性总结失败:', error);
              showNotificationMessage('删除失败');
            }
          }}
        />
      )}

      {/* 分析报告面板 (DocModeRouter: 根据文档类型动态选择面板) */}
      <DocModeRouter
        docType={docType}
        fingerprint={pdfFingerprint}
        show={showAnalysisReportPanel}
        onClose={() => setShowAnalysisReportPanel(false)}
        onNavigateToPage={(page) => onPageChange && onPageChange(page)}
        onDocTypeChange={async (newType) => {
          setDocType(newType);
          try {
            const { apiPatch } = await import('../utils/apiClient');
            await apiPatch(`/api/research/doc-type/${pdfFingerprint}`, { doc_type: newType });
          } catch (e) {
            console.warn('Failed to persist doc_type change:', e);
          }
        }}
      />

      {/* 便利贴编辑浮动窗口 */}
      {showStickyNoteDetail && currentStickyNote && (
        <div style={{
          position: 'fixed',
          left: `${stickyNotePosition.x}px`,
          top: `${stickyNotePosition.y}px`,
          width: '450px',
          backgroundColor: currentStickyNote.noteType === 'question' ? '#FFF9C4' :
                           currentStickyNote.noteType === 'idea' ? '#E3F2FD' : '#FFEBEE',
          borderRadius: '8px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
          zIndex: 2000,
          padding: '20px',
          border: `3px solid ${currentStickyNote.noteType === 'question' ? '#FBC02D' :
                                currentStickyNote.noteType === 'idea' ? '#2196F3' : '#F44336'}`,
          cursor: isDraggingStickyNote ? 'grabbing' : 'default',
          userSelect: 'none'
        }}>
          {/* 标题栏 */}
          <div
            onMouseDown={startDrag}
            style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '15px',
            paddingBottom: '10px',
            borderBottom: '2px solid rgba(0,0,0,0.1)',
            cursor: 'grab'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '20px' }}>
                {currentStickyNote.noteType === 'question' ? '📝' :
                 currentStickyNote.noteType === 'idea' ? '💡' : '⚠️'}
              </span>
              <span style={{ fontWeight: 'bold', fontSize: '16px' }}>
                {currentStickyNote.noteType === 'question' ? '疑问' :
                 currentStickyNote.noteType === 'idea' ? '想法' : '重点'}
              </span>
              <span style={{ fontSize: '12px', color: '#666' }}>
                第 {currentStickyNote.page} 页
              </span>
            </div>
            <button
              onMouseDown={(e) => e.stopPropagation()}
              onClick={() => {
                setShowStickyNoteDetail(false);
                setCurrentStickyNote(null);
              }}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#666',
                padding: '0',
                width: '30px',
                height: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ×
            </button>
          </div>

          {/* 选中内容（如果有） */}
          {currentStickyNote.selectedText && (
            <div style={{
              marginBottom: '15px',
              padding: '10px',
              backgroundColor: 'rgba(255,255,255,0.6)',
              borderRadius: '4px',
              fontSize: '13px',
              fontStyle: 'italic'
            }}>
              <strong>选中内容：</strong>
              <p style={{ margin: '5px 0 0 0' }}>"{currentStickyNote.selectedText}"</p>
            </div>
          )}

          {/* 可编辑内容区域 */}
          <div style={{ marginBottom: '15px' }}>
            <textarea
              value={currentStickyNote.content}
              onMouseDown={(e) => e.stopPropagation()}
              onChange={(e) => {
                setCurrentStickyNote((prev: any) => ({
                  ...prev,
                  content: e.target.value
                }));
              }}
              maxLength={2000}
              style={{
                width: '100%',
                minHeight: '120px',
                maxHeight: '400px',
                padding: '12px',
                border: '2px solid rgba(0,0,0,0.1)',
                borderRadius: '6px',
                fontSize: '14px',
                resize: 'vertical',
                backgroundColor: 'rgba(255,255,255,0.8)',
                fontFamily: 'inherit',
                boxSizing: 'border-box',
                overflow: 'auto'
              }}
              placeholder="输入你的想法..."
            />
            <div style={{
              fontSize: '11px',
              color: '#999',
              textAlign: 'right',
              marginTop: '4px'
            }}>
              {currentStickyNote.content.length} / 2000
            </div>
          </div>

          {/* 操作按钮 */}
          <div
            onMouseDown={(e) => e.stopPropagation()}
            style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: '10px'
          }}>
            <button
              onClick={async () => {
                try {
                  // 使用新的 blocks API 删除
                  const result = await deleteAnnotationFromStorage(currentStickyNote.id) as any;
                  if (result.success) {
                    showNotificationMessage('便利贴已删除');
                    setAnnotations(prev => {
                      const newAnnotations = { ...prev };
                      delete newAnnotations[currentStickyNote.id];
                      return newAnnotations;
                    });
                    setShowStickyNoteDetail(false);
                    setCurrentStickyNote(null);
                  }
                } catch (error: any) {
                  console.error('删除便利贴失败:', error);
                }
              }}
              style={{
                padding: '8px 20px',
                backgroundColor: '#f44336',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              删除
            </button>
            <button
              onClick={async () => {
                try {
                  // 使用新的 blocks API 保存
                  const stickyNoteData = {
                    id: currentStickyNote.id,
                    pdfFingerprint: pdfFingerprint,
                    page: currentStickyNote.page,
                    noteType: currentStickyNote.noteType,
                    content: currentStickyNote.content,
                    selectedText: currentStickyNote.selectedText || '',
                    position: currentStickyNote.position || { x: 0, y: 0 },
                    timestamp: currentStickyNote.timestamp,
                    type: 'sticky_note',
                    tag: currentStickyNote.noteType
                  };

                  const result = await (saveAnnotationsToStorageFn as any)(stickyNoteData);

                  if (result.success) {
                    showNotificationMessage('便利贴已保存');
                    setAnnotations(prev => ({
                      ...prev,
                      [currentStickyNote.id]: currentStickyNote
                    }));
                    setShowStickyNoteDetail(false);
                    setCurrentStickyNote(null);
                  }
                } catch (error: any) {
                  console.error('保存便利贴失败:', error);
                }
              }}
              style={{
                padding: '8px 20px',
                backgroundColor: currentStickyNote.noteType === 'question' ? '#FBC02D' :
                                 currentStickyNote.noteType === 'idea' ? '#2196F3' : '#F44336',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              保存修改
            </button>
          </div>

          <div style={{
            marginTop: '10px',
            fontSize: '11px',
            color: '#999',
            textAlign: 'right'
          }}>
            {new Date(currentStickyNote.timestamp).toLocaleString()}
          </div>
        </div>
      )}
    </div>
  );
}